import { createSlice } from "@reduxjs/toolkit";
import type { Dictionary } from "lodash";
import keyBy from "lodash/keyBy";
import { CostRule, CostRuleInput } from "dora-contracts";
import { createErrorReportingAsyncThunk } from "../../helpers";
import * as api from "../../../pages/InsightsPage/insights-costs-api";

const prefix = "data/insightCostRules";

type State = {
  entities: Dictionary<CostRule>;
  ids: string[];
};

const initialState: State = {
  entities: {},
  ids: [],
};

export const loadInsightCostRules = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async () => api.listRules()
);

export const createInsightCostRule = createErrorReportingAsyncThunk(
  `${prefix}/create`,
  async (input: CostRuleInput) => api.createRule(input)
);

export const updateInsightCostRule = createErrorReportingAsyncThunk(
  `${prefix}/update`,
  async (input: { id: string; input: CostRuleInput }) => {
    await api.updateRule(input.id, input.input);
    return { id: input.id, ...input.input };
  }
);

export const deleteInsightCostRule = createErrorReportingAsyncThunk(
  `${prefix}/delete`,
  async (id: string) => {
    await api.deleteRule(id);
    return id;
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadInsightCostRules.fulfilled, (state, action) => {
        state.entities = keyBy(action.payload, "id");
        state.ids = action.payload.map((x) => x.id);
      })
      .addCase(createInsightCostRule.fulfilled, (state, action) => {
        const rule = action.payload;
        state.entities[rule.id] = rule;
        state.ids.unshift(rule.id);
      })
      .addCase(updateInsightCostRule.fulfilled, (state, action) => {
        state.entities[action.payload.id] = action.payload;
      })
      .addCase(deleteInsightCostRule.fulfilled, (state, action) => {
        const id = action.payload;
        delete state.entities[id];
        state.ids = state.ids.filter((x) => x !== id);
      });
  },
});

export default slice.reducer;
